import React, { useMemo, useCallback } from 'react';
import { AdditionalInfoFieldType, DDCell, UniqueString } from '../../../Types/AdditionalInformation/AdditionalInfoField';
import FloatingLabelTextArea from '../Form/FloatingLabelTextArea/FloatingLabelTextArea';
import DatePicker from '../DatePicker/DatePicker';
import DropDownList from '../DropDownList/DropDownList';
import MultiSelect, { ISelectOption } from '../MultiSelect/MultiSelect';
import ToggleSlider from '../ToggleSlider/ToggleSlider';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import styles from './AdditionalInfoFieldRenderer.module.css';

type AdditionalInfoFieldRendererProps = {
  field: DDCell;
  onValueChange: (fieldId: string, newValue: any) => void;
  readonly?: boolean;
};

const AdditionalInfoFieldRenderer = ({ 
  field, 
  onValueChange,
  readonly = false 
}: AdditionalInfoFieldRendererProps) => {
  
  // Memoize time handling to avoid recreating functions on every render
  const handleTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(':');
    const today = new Date();
    const date = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(hours), parseInt(minutes), 0, 0));
    onValueChange(field.id, date);
  }, [field.id, onValueChange]);

  // Memoise dropdown options to avoid recreating on every render
  const dropdownOptions = useMemo((): IDropDownListItem[] => 
    field.tempData.map((item: UniqueString) => ({
      text: item.Value,
      value: item.Value,
      colour: undefined,
      textColour: undefined
    })), [field.tempData]);

  // Memoise multiselect options to avoid recreating on every render
  const multiselectOptions = useMemo(() => {
    const availableOptions: ISelectOption[] = field.tempData.map((item: UniqueString) => ({
      value: item.Value,
      label: item.Value,
      required: false
    }));
    const selectedOptions: ISelectOption[] = field.selection.map((sel: string) => ({
      value: sel,
      label: sel,
      required: false
    }));
    return { availableOptions, selectedOptions };
  }, [field.tempData, field.selection]);

  // Memoise time value to avoid recalculating on every render
  const timeValue = useMemo(() => 
    field.date ? new Date(field.date).toISOString().slice(11, 16) : '', 
    [field.date]
  );

  const renderField = () => {
    switch (field.type) {
      case AdditionalInfoFieldType.TextArea:
        return (
          <FloatingLabelTextArea
            label={field.header}
            value={field.text || ''}
            onChange={(value) => onValueChange(field.id, value)}
            placeholder="Enter text..."
            readonly={readonly}
          />
        );

      case AdditionalInfoFieldType.DatePicker:
        return (
          <DatePicker
            label={field.header ?? "Select Date"}
            value={field.date ? new Date(field.date) : undefined}
            onChange={(date) => onValueChange(field.id, date)}
            readonly={readonly}
          />
        );

      case AdditionalInfoFieldType.TimePicker:
        return (
          <div className={styles.fieldWrapper}>
            <label className={styles.label}>{field.header}</label>
            <input
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              className={styles.timeInput}
              disabled={readonly}
            />
          </div>
        );

      case AdditionalInfoFieldType.DropDownList:
        return (
          <div className={styles.fieldWrapper}>
            <label className={styles.label}>{field.header}</label>
            <DropDownList
              placeholder="Select option..."
              value={field.text || ''}
              data={dropdownOptions}
              onChange={(item) => onValueChange(field.id, item.value)}
              readonly={readonly}
            />
          </div>
        );

      case AdditionalInfoFieldType.MultiSelect:
        return (
          <div className={styles.fieldWrapper}>
            <label className={styles.label}>{field.header}</label>
            <MultiSelect
              availableOptions={multiselectOptions.availableOptions}
              selectedOptions={multiselectOptions.selectedOptions}
              onChange={(selected: ISelectOption[]) => onValueChange(field.id, selected.map(s => s.value))}
              readonly={readonly}
            />
          </div>
        );

      case AdditionalInfoFieldType.Switch:
        return (
          <div className={styles.fieldWrapper}>
            <label className={styles.label}>{field.header}</label>
            <ToggleSlider
              isOn={field.state}
              onToggle={(newState: boolean) => onValueChange(field.id, newState)}
              disabled={readonly}
            />
          </div>
        );

      default:
        return (
          <FloatingLabelTextArea
            label={field.header}
            value={field.text || ''}
            onChange={(value) => onValueChange(field.id, value)}
            placeholder="Enter text..."
            readonly={readonly}
          />
        );
    }
  };

  return <div className={styles.container}>{renderField()}</div>;
};

export default AdditionalInfoFieldRenderer;

